import { fetchWithAuth } from './adminApi'

export type IntegrationStatus = 'connecting' | 'active' | 'error' | 'disconnected'
export type IntegrationProvider = 'bigcommerce' | 'shipstation' | 'easypost_carrier'
export type OutboxStatus = 'held' | 'in_doubt' | 'failed' | 'pending' | 'sent' | 'discarded'

export interface IntegrationSummary {
  id: string
  shop_id: string
  shop_name: string | null
  provider: IntegrationProvider
  status: IntegrationStatus
  display_name: string | null
  external_account_id: string
  webhooks_registered: boolean
  last_sync_at: string | null
  next_reconcile_at: string | null
  last_error: string | null
  last_error_at: string | null
  created_at: string | null
  open_gaps: number
  synced_listings: number
  outbox_attention: number
}
export interface IntegrationList {
  items: IntegrationSummary[]
  total: number
  page: number
  page_size: number
  writes_enabled: boolean
}
export interface OutboxRow {
  id: string
  integration_id: string
  shop_id: string
  kind: string
  status: OutboxStatus
  order_id: string | null
  target_product_id: string | null
  target_variant_id: string | null
  delta: number | null
  attempts: number
  last_error: string | null
  created_at: string | null
  sent_at: string | null
  reason: string | null
}
export interface SyncRun {
  id: string
  kind: string
  status: string
  total: number
  processed: number
  created_count: number
  updated_count: number
  unchanged_count: number
  skipped_count: number
  failed_count: number
  error_summary: string | null
  started_at: string | null
  finished_at: string | null
}
export interface WebhookEvent {
  id: string
  scope: string
  resource_id: string | null
  status: string
  attempts: number
  last_error: string | null
  received_at: string | null
}
export interface IntegrationDetail {
  integration: IntegrationSummary
  settings: Record<string, unknown>
  scopes: Record<string, unknown> | null
  outbox_by_status: Record<string, number>
  gaps_by_kind: Record<string, number>
  recent_runs: SyncRun[]
  recent_events: WebhookEvent[]
  attention: OutboxRow[]
}

async function get<T>(path: string): Promise<T> {
  const result = await fetchWithAuth<T>(`/admin/integrations${path}`)
  if (!result.ok || result.data === undefined)
    throw new Error(result.error?.message || 'Integrations request failed')
  return result.data
}

export const integrations = {
  list: (filters: { status?: string; provider?: string; shop_id?: string; page?: number }) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(filters))
      if (value !== undefined && value !== '') params.set(key, String(value))
    return get<IntegrationList>(`?${params}`)
  },
  detail: (id: string) => get<IntegrationDetail>(`/${encodeURIComponent(id)}`),
  outbox: (status: OutboxStatus, integrationId?: string) => {
    const params = new URLSearchParams({ status })
    if (integrationId) params.set('integration_id', integrationId)
    return get<{ items: OutboxRow[]; writes_enabled: boolean }>(`/outbox?${params}`)
  },
}
